<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-title">修复产品配置</div>
      <div class="section-actions">
        <button class="add-btn" type="button" @click="openRepairModal">
          + 新增修复产品
        </button>
      </div>
    </div>

    <div v-if="listLoading" class="list-loading">加载中...</div>
    <div v-else class="form-group repair-list-section">
      <table class="edit-repair-table">
        <thead>
          <tr>
            <th>修复产品名称</th>
            <th class="edit-repair-action-col">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!repairs.length">
            <td colspan="2" class="empty-cell">暂无修复产品</td>
          </tr>
          <tr v-for="repair in repairs" :key="repair.repairId">
            <td>
              <div
                v-if="editingRepairId === repair.repairId"
                class="repair-name-edit"
              >
                <input
                  v-model.trim="repairEditDraft"
                  class="form-input repair-name-input"
                  @keyup.enter="confirmEditRepair(repair)"
                />
              </div>
              <span v-else>{{ repair.repairName }}</span>
            </td>
            <td class="edit-repair-action-col">
              <template v-if="editingRepairId === repair.repairId">
                <button
                  type="button"
                  class="mini-btn mini-btn-confirm"
                  :disabled="savingRepairEditId === repair.repairId"
                  @click="confirmEditRepair(repair)"
                >
                  {{
                    savingRepairEditId === repair.repairId
                      ? "提交中..."
                      : "确认"
                  }}
                </button>
                <button
                  type="button"
                  class="mini-btn mini-btn-cancel"
                  @click="cancelEditRepair"
                >
                  取消
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="mini-btn mini-btn-edit"
                  @click="startEditRepair(repair)"
                >
                  修改
                </button>
                <button
                  type="button"
                  class="mini-btn mini-btn-delete"
                  @click="deleteEditRepair(repair)"
                >
                  删除
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="showRepairModal"
      class="modal-overlay"
      @click.self="closeRepairModal"
    >
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">新增修复产品</span>
          <span class="modal-close" @click="closeRepairModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">修复产品名称</label>
            <input
              v-model.trim="repairForm.repairName"
              class="form-input"
            />
          </div>
          <button
            class="modal-save-btn"
            type="button"
            :disabled="savingRepair"
            @click="saveRepair"
          >
            {{ savingRepair ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  addRepairs,
  deleteRepair,
  listAllRepairs,
  updateRepair,
} from "@/api/repair";

const SUCCESS_CODE = 20000;

export default {
  name: "RepairProject",
  data() {
    return {
      listLoading: false,
      savingRepair: false,
      showRepairModal: false,
      repairForm: {
        repairName: "",
      },
      editingRepairId: null,
      savingRepairEditId: null,
      repairEditDraft: "",
      repairs: [],
    };
  },
  mounted() {
    this.fetchRepairList();
  },
  methods: {
    showMessage(type, text) {
      this.$message({
        message: text,
        type,
      });
    },
    openRepairModal() {
      this.repairForm = { repairName: "" };
      this.showRepairModal = true;
    },
    closeRepairModal() {
      this.showRepairModal = false;
    },
    isBizError(res) {
      return res && res.code !== undefined && res.code !== SUCCESS_CODE;
    },
    normalizeRepair(repair) {
      if (!repair) return null;
      if (typeof repair === "string") {
        return { repairId: repair, repairName: repair };
      }
      return {
        repairId: String(repair.repairId || repair.id || ""),
        repairName: repair.repairName || repair.name || "",
      };
    },
    normalizeRepairList(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => this.normalizeRepair(item)).filter(Boolean);
    },
    mapRepairList(list) {
      return this.normalizeRepairList(list);
    },
    flattenRepairsFromProjects(projects) {
      const map = new Map();
      (projects || []).forEach((item) => {
        const packages = item.packages || item.repairs || [];
        this.normalizeRepairList(packages).forEach((repair) => {
          if (repair.repairId && !map.has(repair.repairId)) {
            map.set(repair.repairId, repair);
          }
        });
      });
      return Array.from(map.values());
    },
    mergeCreatedRepair(res) {
      const created = this.normalizeRepair(res && res.data);
      if (!created || !created.repairId) return;
      const exists = this.repairs.some((r) => r.repairId === created.repairId);
      if (!exists) {
        this.repairs = [...this.repairs, created];
      }
    },
    async fetchRepairList() {
      this.listLoading = true;
      try {
        let repairs = null;

        try {
          const res = await listAllRepairs();
          if (!this.isBizError(res)) {
            repairs = this.mapRepairList(res && res.data);
          }
        } catch (e) {
          // listAllRepairs 不可用时降级到项目接口
        }

        this.repairs = repairs;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.repairs = [];
      } finally {
        this.listLoading = false;
      }
    },
    async saveRepair() {
      if (!this.repairForm.repairName) {
        this.showMessage("warning", "请输入修复产品名称");
        return;
      }

      this.savingRepair = true;
      try {
        const res = await addRepairs({
          repairName: this.repairForm.repairName,
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "修复产品新增失败");
          return;
        }

        this.showMessage("success", (res && res.msg) || "修复产品新增成功");
        this.closeRepairModal();
        await this.fetchRepairList();
        this.mergeCreatedRepair(res);
      } catch (error) {
        const msg =
          (error && error.code === 40000 && (error.msg || error.message)) ||
          error.message ||
          "修复产品新增失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingRepair = false;
      }
    },
    startEditRepair(repair) {
      this.editingRepairId = repair.repairId;
      this.repairEditDraft = repair.repairName;
    },
    cancelEditRepair() {
      this.editingRepairId = null;
      this.repairEditDraft = "";
    },
    async confirmEditRepair(repair) {
      const name = (this.repairEditDraft || "").trim();
      if (!name) {
        this.showMessage("warning", "修复产品名称不能为空");
        return;
      }

      this.savingRepairEditId = repair.repairId;
      try {
        const res = await updateRepair({
          repairId: repair.repairId,
          repairName: name,
        });

        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "修复产品修改失败");
          return;
        }

        const target = this.repairs.find((r) => r.repairId === repair.repairId);
        if (target) {
          target.repairName = name;
        }

        this.editingRepairId = null;
        this.repairEditDraft = "";
        this.showMessage("success", (res && res.msg) || "修复产品名称已修改");
        await this.fetchRepairList();
      } catch (error) {
        const msg =
          (error && error.code === 40000 && (error.msg || error.message)) ||
          error.message ||
          "修复产品修改失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingRepairEditId = null;
      }
    },
    deleteEditRepair(repair) {
      this.$confirm(`确认删除修复产品「${repair.repairName}」？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await deleteRepair({
              repairId: repair.repairId,
            });

            if (this.isBizError(res)) {
              this.showMessage("error", res.msg || "修复产品删除失败");
              return;
            }

            if (this.editingRepairId === repair.repairId) {
              this.cancelEditRepair();
            }

            this.showMessage("success", (res && res.msg) || "修复产品已删除");
            await this.fetchRepairList();
          } catch (error) {
            const msg =
              (error && error.code === 40000 && (error.msg || error.message)) ||
              error.message ||
              "修复产品删除失败，请稍后重试";
            this.showMessage("error", msg);
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.list-loading {
  padding: 40px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.empty-cell {
  padding: 24px 0;
  color: #999;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.repair-list-section {
  margin-bottom: 0;
}

.repair-list-section .form-label {
  font-size: 16px;
  font-weight: 600;
}

.add-btn {
  margin-top: 0;
  border: none;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 14px;
  font-size: 14px;
  cursor: pointer;
}

.add-btn:hover {
  background: #096dd9;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 12px;
  width: 420px;
  max-width: calc(100% - 32px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.modal-save-btn {
  width: 100%;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: #1890ff;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.modal-save-btn:hover {
  background: #096dd9;
}

.edit-repair-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #eef1f6;
  font-size: 13px;
}

.edit-repair-table th,
.edit-repair-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 10px 12px;
  text-align: left;
  vertical-align: middle;
}

.edit-repair-table th {
  background: #f8f9fc;
  font-weight: 600;
  color: #616f84;
}

.edit-repair-table tbody tr:hover td {
  background: #f0f7ff;
}

.edit-repair-action-col {
  width: 140px;
  white-space: nowrap;
}

.repair-name-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.repair-name-input {
  flex: 1;
  min-width: 0;
}

.mini-btn {
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
}

.mini-btn:last-child {
  margin-right: 0;
}

.mini-btn-edit {
  background: #fff;
  color: #409eff;
  border: 1px solid #409eff;
}

.mini-btn-confirm {
  background: #52c41a;
  color: #fff;
  border: 1px solid #52c41a;
}

.mini-btn-confirm:hover {
  background: #389e0d;
}

.mini-btn-confirm:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.mini-btn-cancel {
  background: #fff;
  color: #666;
  border: 1px solid #d9d9d9;
}

.mini-btn-delete {
  background: #ff4d4f;
  color: #fff;
}

.form-input:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}
</style>
